import { z } from 'zod';

// Mediciones por ítem del checklist y datos del equipo de la OT (PR-C).
// Se guardan como JSONB: `actividad.variables` / `pauta.datos_equipo` (definición, VariableDef[]) y
// `detalle_actividades.variables` / `ot.datos_equipo` (copia con valor, VariableValor[]).
// null o [] significan «sin variables»: todo el código los trata igual.

export const TIPOS_VARIABLE = ['numero', 'texto'] as const;
export type TipoVariable = (typeof TIPOS_VARIABLE)[number];

export interface VariableDef {
  clave: string;
  etiqueta: string;
  unidad: string | null;
  tipo: TipoVariable;
}

export interface VariableValor extends VariableDef {
  valor: string | null;
}

export const VARIABLES_MAX = 12;
export const VARIABLE_ETIQUETA_MAX = 60;
export const VARIABLE_UNIDAD_MAX = 20;
export const VARIABLE_TEXTO_MAX = 200;
/** Texto que se muestra y se imprime cuando una medición no tiene valor. */
export const VARIABLE_VACIA = '—';

/** Error de validación de un valor: el mensaje ya está listo para mostrarse al usuario (400). */
export class VariableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VariableError';
  }
}

const quitarNul = (s: string): string => s.split('\u0000').join('');
const sinTildes = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Etiqueta → clave estable: minúsculas, sin tildes, solo [a-z0-9_]. Nunca vacía. */
export function claveDesdeEtiqueta(etiqueta: string): string {
  const base = sinTildes(etiqueta)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return base.length > 0 ? base : 'campo';
}

const claveBase = (s: string): string => s.trim().replace(/[^a-z0-9_]/g, '').slice(0, 80);

const VariableDefInputItemSchema = z.object({
  clave: z.string().max(80).optional(),
  etiqueta: z
    .string()
    .refine((s) => !s.includes('\u0000'), 'La etiqueta tiene caracteres no válidos')
    .transform((s) => s.trim().replace(/\s+/g, ' '))
    .pipe(z.string().min(1, 'La etiqueta es obligatoria').max(VARIABLE_ETIQUETA_MAX, `La etiqueta admite hasta ${VARIABLE_ETIQUETA_MAX} caracteres`)),
  unidad: z
    .string()
    .nullish()
    .refine((s) => !(s ?? '').includes('\u0000'), 'La unidad tiene caracteres no válidos')
    .transform((s) => {
      const t = (s ?? '').trim();
      return t.length === 0 ? null : t;
    })
    .pipe(z.string().max(VARIABLE_UNIDAD_MAX, `La unidad admite hasta ${VARIABLE_UNIDAD_MAX} caracteres`).nullable()),
  tipo: z.enum(TIPOS_VARIABLE),
});

/**
 * Lista de definiciones que envía el editor. Valida (máx. 12, etiquetas únicas sin distinguir mayúsculas
 * ni tildes, NUL fuera) y completa la `clave`: la enviada se conserva (renombrar no la cambia) y la
 * ausente se genera desde la etiqueta, con sufijo `_2`, `_3` si se repite.
 */
export const VariablesDefInputSchema = z
  .array(z.any())
  .max(VARIABLES_MAX, `Máximo ${VARIABLES_MAX} por lista`)
  .transform((items, ctx): VariableDef[] => {
    const out: VariableDef[] = [];
    const etiquetas = new Set<string>();
    const claves = new Set<string>();
    for (let i = 0; i < items.length; i++) {
      const r = VariableDefInputItemSchema.safeParse(items[i]);
      if (!r.success) {
        for (const issue of r.error.issues) ctx.addIssue({ ...issue, path: [i, ...issue.path] });
        return z.NEVER;
      }
      const { etiqueta, unidad, tipo } = r.data;
      const clave0 = r.data.clave !== undefined && claveBase(r.data.clave) !== '' ? claveBase(r.data.clave) : claveDesdeEtiqueta(etiqueta);
      const huella = sinTildes(etiqueta).toLowerCase();
      if (etiquetas.has(huella)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `La etiqueta «${etiqueta}» está repetida`, path: [i, 'etiqueta'] });
        return z.NEVER;
      }
      etiquetas.add(huella);
      let clave = clave0;
      for (let n = 2; claves.has(clave); n++) clave = `${clave0}_${n}`;
      claves.add(clave);
      out.push({ clave, etiqueta, unidad, tipo });
    }
    return out;
  });

export const SetActividadVariablesSchema = z.object({ variables: VariablesDefInputSchema });
export const SetDatosEquipoPautaSchema = z.object({ campos: VariablesDefInputSchema });

/** Body de los PUT de ejecución: se mezclan solo las claves enviadas. */
export const ValoresVariablesBodySchema = z.object({
  valores: z.record(z.string().max(80), z.string().max(1000).nullable()),
});

export type SetActividadVariablesInput = z.infer<typeof SetActividadVariablesSchema>;
export type SetDatosEquipoPautaInput = z.infer<typeof SetDatosEquipoPautaSchema>;
export type ValoresVariablesBody = z.infer<typeof ValoresVariablesBodySchema>;

const NUMERO_RE = /^[-+]?\d+(?:[.,]\d+)?$/;
// «24.000» o «1.500.000»: en Chile el punto separa miles, pero acá se leería como decimal (24,000). Se rechaza.
// Un cero inicial («0.001») no puede ser grupo de miles, así que sigue siendo un decimal válido.
const MILES_RE = /^[-+]?(?!0\.)\d{1,3}(?:\.\d{3})+$/;

/**
 * Valida y deja canónico el valor de una variable: número con punto (`"85.5"`), texto recortado.
 * Vacío o null → null. Lanza `VariableError` con el mensaje del contrato si no es válido.
 */
export function normalizarValorVariable(def: VariableDef, raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const limpio = quitarNul(raw).trim();
  if (limpio === '') return null;
  if (def.tipo === 'numero') {
    if (MILES_RE.test(limpio)) {
      throw new VariableError(`El valor de «${def.etiqueta}» debe escribirse sin puntos de miles (ej.: 24000)`);
    }
    if (!NUMERO_RE.test(limpio) || limpio.length > 30) {
      throw new VariableError(`El valor de «${def.etiqueta}» debe ser un número`);
    }
    const canonico = limpio.replace(',', '.');
    return canonico.startsWith('+') ? canonico.slice(1) : canonico;
  }
  if (limpio.length > VARIABLE_TEXTO_MAX) {
    throw new VariableError(`El valor de «${def.etiqueta}» admite hasta ${VARIABLE_TEXTO_MAX} caracteres`);
  }
  return limpio;
}

/** Valor listo para mostrar: números con coma decimal seguidos de la unidad; vacío = «—». */
export function formatearValorVariable(v: VariableValor): string {
  if (v.valor === null || v.valor === '') return VARIABLE_VACIA;
  if (v.tipo === 'numero') {
    const n = v.valor.replace('.', ',');
    return v.unidad ? `${n} ${v.unidad}` : n;
  }
  return v.unidad ? `${v.valor} ${v.unidad}` : v.valor;
}

/** Copia las definiciones con `valor: null` (lo que recibe cada OT al poblar el checklist). */
export function construirValoresDesdeDefs(defs: readonly VariableDef[]): VariableValor[] {
  return defs.map((d) => ({ clave: d.clave, etiqueta: d.etiqueta, unidad: d.unidad, tipo: d.tipo, valor: null }));
}

/**
 * Mezcla `nuevos` sobre `actuales`: solo cambian las claves enviadas. Clave desconocida o valor
 * inválido → `VariableError`. No muta la entrada.
 */
export function mezclarValoresVariables(
  actuales: readonly VariableValor[],
  nuevos: Readonly<Record<string, string | null>>,
  opts?: { donde?: 'item' | 'datos-equipo' },
): VariableValor[] {
  const conocidas = new Set(actuales.map((a) => a.clave));
  for (const clave of Object.keys(nuevos)) {
    // El mensaje no expone la clave interna (el usuario nunca la ve).
    if (!conocidas.has(clave)) {
      throw new VariableError(
        opts?.donde === 'datos-equipo'
          ? 'Un campo enviado no existe en los datos del equipo'
          : 'Un campo enviado no existe en este ítem',
      );
    }
  }
  return actuales.map((a) =>
    Object.prototype.hasOwnProperty.call(nuevos, a.clave)
      ? { ...a, valor: normalizarValorVariable(a, nuevos[a.clave]) }
      : { ...a },
  );
}

/** Valor del JSONB como texto: un número finito (85) se lee como «85»; lo demás vacío o inválido es null. */
function valorComoTexto(raw: unknown): string | null {
  if (typeof raw === 'string') return raw !== '' ? raw : null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return null;
}

/**
 * Lee un JSONB de la BD como VariableValor[] tolerante: null, no-arreglo o elementos mal formados dan
 * [] / se descartan (nunca revienta una lectura del checklist).
 */
export function leerVariablesValor(raw: unknown): VariableValor[] {
  if (!Array.isArray(raw)) return [];
  const out: VariableValor[] = [];
  for (const e of raw) {
    if (e === null || typeof e !== 'object') continue;
    const o = e as Record<string, unknown>;
    if (typeof o.clave !== 'string' || typeof o.etiqueta !== 'string') continue;
    out.push({
      clave: o.clave,
      etiqueta: o.etiqueta,
      unidad: typeof o.unidad === 'string' && o.unidad !== '' ? o.unidad : null,
      tipo: o.tipo === 'texto' ? 'texto' : 'numero',
      valor: valorComoTexto(o.valor),
    });
  }
  return out;
}

/** Igual que `leerVariablesValor` pero para definiciones (descarta el valor). */
export function leerVariablesDef(raw: unknown): VariableDef[] {
  return leerVariablesValor(raw).map(({ clave, etiqueta, unidad, tipo }) => ({ clave, etiqueta, unidad, tipo }));
}

/** Mediciones vacías de los ítems ya resueltos (OK o NOK): alimenta el aviso «Faltan N mediciones». */
export function contarMedicionesFaltantes(
  items: ReadonlyArray<{ estadoActividad?: string | null; variables?: ReadonlyArray<VariableValor> | null }>,
): number {
  let n = 0;
  for (const it of items) {
    if (it.estadoActividad !== 'OK' && it.estadoActividad !== 'NOK') continue;
    for (const v of it.variables ?? []) if (v.valor === null || v.valor === '') n++;
  }
  return n;
}

/** «Faltan 3 mediciones» / «Falta 1 medición»; null si no falta ninguna. */
export function textoMedicionesFaltantes(n: number): string | null {
  if (n <= 0) return null;
  return n === 1 ? 'Falta 1 medición' : `Faltan ${n} mediciones`;
}
